import { useMemo } from 'react';

import { useQuery } from '@tanstack/react-query';

import { SEX } from '@/constants';
import {
  FREEZE_OPTION_VALUES,
  NON_EU_COUNTRY_VALUES,
  PREGNANT_OPTION_VALUES,
  SHIP_OPTION_VALUES,
  TRANSFER_OPTION_VALUES,
} from '@/enums';
import {
  CheckmarkValueRenderer,
  DonorPartnershipCellRenderer,
  PastDueCellRenderer,
} from '@/grid/cell-renderers';
import { ColDef } from '@/grid/types';
import {
  clinicRanchHaulinFormatter,
  dateFormatter,
  nonEuCountryFormatter,
  pregnantFormatter,
} from '@/grid/value-formatters';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { useStringMap } from '@/hooks/useStringMap';
import { DonorSchedule } from '@/rtk-query.gql';
import { stringMapOptions } from '@/services/lookups';
import {
  SUBFAMILY_FILTER_VALUES,
  SUBFAMILY_LABELS,
} from '@/utils/subfamilyValues';

export const defaultColDef: ColDef<DonorSchedule> = {
  autoHeaderHeight: true,
  filter: true,
};

const formatSemenSex = (
  sex?: number | string | null,
  useConventionalLabel = false,
) => {
  const numericSex = typeof sex === 'string' ? Number(sex) : sex;

  if (numericSex === SEX.Female) return 'Female';
  if (numericSex === SEX.Male) return 'Male';
  if (numericSex === SEX.Unknown) {
    return useConventionalLabel ? 'Conventional' : 'Unknown';
  }
  return '';
};

export function useDonorScheduleColumnDefs(): ColDef<DonorSchedule>[] {
  const isRest = useFeatureFlag('29169_donor_scheduling_to_rest');
  const isAddColumnsEnabled = useFeatureFlag('Donor_Scheduling_Add_Columns');
  const isInactiveLookupEnabled = useFeatureFlag('DS_Cannot_Lookup_Inactive');
  const isSemenConventionalTypeEnabled = useFeatureFlag(
    'Semen_Conventional_Type',
  );
  const isExportColumnEnabled = true; // useFeatureFlag('ds_export_column');
  const exportStringMap = useStringMap('donor_schedule_export');
  const { data: subfamilyOptions } = useQuery({
    ...stringMapOptions({ objectTypeCode: 10000, name: 'tog_subfamily' }),
    enabled: isRest,
  });

  return useMemo(() => {
    const subfamilies = subfamilyOptions ?? [];
    const subfamilyLabels = Object.fromEntries(
      subfamilies.map((option) => [Number(option.key), option.value]),
    );
    const subfamilyValues = subfamilies.map((option) => Number(option.key));
    const exportOptions = exportStringMap.options ?? [];
    const exportLabels = new Map(
      exportOptions.map((option) => [option.value, option.label]),
    );

    return [
      {
        field: '',
        headerName: '',
        initialWidth: 40,
        width: 40,
        maxWidth: 40,
        suppressHeaderMenuButton: true,
        checkboxSelection: true,
        showDisabledCheckboxes: true,
        suppressExport: true,
        sortable: false,
        filter: false,
      },
      {
        field: 'stateCode',
        headerName: 'Status',
        wrapHeaderText: true,
        sortable: true,
        resizable: true,
        initialWidth: 90,
        hide: !(isInactiveLookupEnabled && isRest),
        filter: 'agSetColumnFilter',
        filterParams: {
          values: ['Active', 'Inactive'],
          defaultToNothingSelected: true,
        },
        valueGetter: (params) => {
          return params.data?.stateCode === 0 ? 'Active' : 'Inactive';
        },
      },
      {
        field: 'date',
        headerName: 'Date',
        wrapHeaderText: true,
        sortable: true,
        resizable: true,
        initialWidth: 80,
        filter: 'agDateColumnFilter',
        valueFormatter: dateFormatter,
        sort: 'desc',
      },

      {
        field: 'csr.fullName',
        headerName: 'CSR',
        wrapHeaderText: true,
        sortable: true,
        resizable: true,
        initialWidth: 80,
        filter: 'agTextColumnFilter',
      },
      {
        field: 'fertilizationSite.name',
        headerName: 'Fert Site',
        wrapHeaderText: true,
        sortable: true,
        resizable: true,
        initialWidth: 80,
        filter: 'agTextColumnFilter',
      },
      {
        field: 'aspirationSite.name',
        headerName: 'Asp Site',
        wrapHeaderText: true,
        sortable: true,
        resizable: true,
        initialWidth: 80,
        filter: 'agTextColumnFilter',
      },
      {
        field: 'dow',
        headerName: 'DOW',
        wrapHeaderText: true,
        sortable: true,
        resizable: true,
        initialWidth: 80,
        filter: 'agTextColumnFilter',
      },
      {
        field: 'donor',
        headerName: 'Donor',
        wrapHeaderText: true,
        sortable: true,
        resizable: true,
        initialWidth: 80,
        cellRenderer: DonorPartnershipCellRenderer,
        filter: 'agTextColumnFilter',
        valueGetter: (params) => {
          const donor = params.data?.donor;
          if (!donor) return '';
          return donor.name || donor.fullName || donor.id || '';
        },
      },
      {
        field: 'donor.registrationNumber',
        headerName: 'Registration #',
        wrapHeaderText: true,
        sortable: true,
        resizable: true,
        initialWidth: 80,
        filter: 'agTextColumnFilter',
        cellDataType: 'text',
      },
      {
        field: 'donorLocation',
        headerName: 'Donor Location',
        wrapHeaderText: true,
        sortable: true,
        resizable: true,
        initialWidth: 80,
        filter: 'agTextColumnFilter',
      },
      {
        field: 'sire.name',
        headerName: 'Sire',
        wrapHeaderText: true,
        sortable: true,
        resizable: true,
        initialWidth: 80,
        filter: 'agTextColumnFilter',
      },
      {
        field: 'semenTypeValue',
        headerName: 'Semen Type',
        wrapHeaderText: true,
        sortable: true,
        resizable: true,
        initialWidth: 80,
        filter: false,
      },
      {
        field: 'backupSire.name',
        headerName: 'Backup Sire',
        wrapHeaderText: true,
        sortable: true,
        resizable: true,
        initialWidth: 80,
        filter: 'agTextColumnFilter',
      },
      {
        field: 'programName',
        headerName: 'Program Name',
        wrapHeaderText: true,
        sortable: true,
        resizable: true,
        initialWidth: 100,
        filter: 'agTextColumnFilter',
      },
      {
        field: 'owner',
        headerName: 'Account',
        wrapHeaderText: true,
        sortable: true,
        resizable: true,
        initialWidth: 199,
        filter: 'agTextColumnFilter',
        cellRenderer: PastDueCellRenderer,
        cellClass: 'custom-cell__past-due',
        valueGetter: (params) => {
          const owner = params.data?.owner;
          if (!owner) return '';
          return owner.name || owner.fullName || owner.code || '';
        },
      },
      {
        field: 'owner.code',
        headerName: 'Ranch ID',
        wrapHeaderText: true,
        sortable: true,
        resizable: true,
        initialWidth: 80,
        filter: 'agTextColumnFilter',
      },
      {
        field: 'semen.secondaryOwner',
        headerName: 'Secondary Owner',
        wrapHeaderText: true,
        sortable: true,
        resizable: true,
        initialWidth: 160,
        filter: 'agTextColumnFilter',
      },
      {
        field: 'donor.yard.name',
        headerName: 'Yard',
        wrapHeaderText: true,
        sortable: true,
        resizable: true,
        initialWidth: 80,
        filter: 'agTextColumnFilter',
      },
      {
        field: 'useDonorPartnerships',
        headerName: 'Use Donor Partnerships',
        wrapHeaderText: true,
        sortable: true,
        resizable: true,
        initialWidth: 80,
        filter: 'agTextColumnFilter', // TODO
        cellRenderer: CheckmarkValueRenderer,
        cellClass: 'custom-cell__checkmark',
      },
      {
        field: 'billingAccount.name',
        headerName: 'Billing Account',
        wrapHeaderText: true,
        sortable: true,
        resizable: true,
        initialWidth: 80,
        filter: 'agTextColumnFilter', // TODO
      },
      {
        field: 'implantTypeValue',
        headerName: 'Implant Type',
        wrapHeaderText: true,
        sortable: true,
        resizable: true,
        initialWidth: 80,
        filter: 'agTextColumnFilter',
      },
      {
        field: 'scheduleMaster.name',
        headerName: 'Schedule',
        wrapHeaderText: true,
        sortable: true,
        resizable: true,
        initialWidth: 80,
        filter: 'agTextColumnFilter',
      },
      {
        field: 'drug.name',
        headerName: 'FSH Drug 1',
        wrapHeaderText: true,
        sortable: true,
        resizable: true,
        initialWidth: 80,
        filter: 'agTextColumnFilter',
      },
      {
        field: 'soSchedule.description',
        headerName: 'SO Schedule',
        wrapHeaderText: true,
        sortable: true,
        resizable: true,
        initialWidth: 160,
        filter: 'agTextColumnFilter',
      },
      {
        field: 'scheduled',
        headerName: 'Schedule Sent?',
        wrapHeaderText: true,
        sortable: true,
        resizable: true,
        initialWidth: 80,
        filter: 'agTextColumnFilter',
        cellRenderer: CheckmarkValueRenderer,
        cellClass: 'custom-cell__checkmark',
      },
      {
        field: 'clinicRanchHaulin',
        headerName: 'Clinic/Ranch/Haulin',
        wrapHeaderText: true,
        sortable: true,
        resizable: true,
        initialWidth: 80,
        filter: 'agTextColumnFilter',
        valueFormatter: clinicRanchHaulinFormatter,
      },
      {
        field: 'billDrugs',
        headerName: 'Bill Drugs?',
        wrapHeaderText: true,
        sortable: true,
        resizable: true,
        initialWidth: 80,
        filter: 'agTextColumnFilter',
        cellRenderer: CheckmarkValueRenderer,
        cellClass: 'custom-cell__checkmark',
      },
      {
        field: 'freezeInstructions',
        headerName: 'Freeze Instructions',
        wrapHeaderText: true,
        sortable: true,
        resizable: true,
        initialWidth: 80,
        filter: 'agTextColumnFilter',
      },
      {
        field: 'id',
        headerName: 'ID',
        hide: true,
        filter: 'agTextColumnFilter',
      },
      {
        field: 'owner.priceTier',
        headerName: 'Price Tier',
        hide: true,
        filter: 'agTextColumnFilter',
      },
      {
        field: 'revision',
        headerName: 'Revision #',
        filter: 'agTextColumnFilter',
      },
      {
        field: 'revisionComment',
        headerName: 'Revision Comments',
        filter: 'agTextColumnFilter',
      },
      {
        field: 'ship',
        headerName: 'Ship?',
        filter: 'agSetColumnFilter',
        width: 80,
        filterParams: {
          values: [
            SHIP_OPTION_VALUES.SHIP,
            SHIP_OPTION_VALUES.SHIP_ALL,
            SHIP_OPTION_VALUES.SHIP_DAY_5,
            SHIP_OPTION_VALUES.SHIP_STRAW_ALL,
            SHIP_OPTION_VALUES.SHIP_STRAW,
          ],
          valueFormatter: (params: { value: number }) => {
            switch (params.value) {
              case SHIP_OPTION_VALUES.SHIP:
                return 'Ship';
              case SHIP_OPTION_VALUES.SHIP_ALL:
                return 'Ship All';
              case SHIP_OPTION_VALUES.SHIP_DAY_5:
                return 'Ship Day 5';
              case SHIP_OPTION_VALUES.SHIP_STRAW_ALL:
                return 'Ship Straw All';
              case SHIP_OPTION_VALUES.SHIP_STRAW:
                return 'Ship Straw';
              default:
                return shipValue.toString();
            }
          },
          defaultToNothingSelected: true,
          suppressSelectAll: false,
        },
        valueGetter: (params) => {
          const shipValue = params.data?.ship;
          if (!shipValue) return '';

          switch (shipValue) {
            case SHIP_OPTION_VALUES.SHIP:
              return 'Ship';
            case SHIP_OPTION_VALUES.SHIP_ALL:
              return 'Ship All';
            case SHIP_OPTION_VALUES.SHIP_DAY_5:
              return 'Ship Day 5';
            case SHIP_OPTION_VALUES.SHIP_STRAW_ALL:
              return 'Ship Straw All';
            case SHIP_OPTION_VALUES.SHIP_STRAW:
              return 'Ship Straw';
            default:
              return shipValue.toString();
          }
        },
      },
      {
        field: 'shipmentGroup.name',
        headerName: 'Shipment Group',
        width: 120,
        filter: 'agTextColumnFilter',
      },
      {
        field: 'freeze',
        headerName: 'Freeze?',
        filter: 'agSetColumnFilter',
        width: 80,
        filterParams: {
          values: [
            FREEZE_OPTION_VALUES.FREEZE,
            FREEZE_OPTION_VALUES.FREEZE_ONLY,
            FREEZE_OPTION_VALUES.FREEZE_1,
            FREEZE_OPTION_VALUES.FREEZE_2,
            FREEZE_OPTION_VALUES.FREEZE_3,
            FREEZE_OPTION_VALUES.FREEZE_4,
            FREEZE_OPTION_VALUES.FREEZE_5,
            FREEZE_OPTION_VALUES.FREEZE_6,
            FREEZE_OPTION_VALUES.FREEZE_7,
            FREEZE_OPTION_VALUES.FREEZE_8,
            FREEZE_OPTION_VALUES.FREEZE_9,
            FREEZE_OPTION_VALUES.FREEZE_10,
          ],
          valueFormatter: (params: { value: number }) => {
            switch (params.value) {
              case FREEZE_OPTION_VALUES.FREEZE:
                return 'Freeze';
              case FREEZE_OPTION_VALUES.FREEZE_ONLY:
                return 'Freeze Only';
              case FREEZE_OPTION_VALUES.FREEZE_1:
                return 'Freeze 1';
              case FREEZE_OPTION_VALUES.FREEZE_2:
                return 'Freeze 2';
              case FREEZE_OPTION_VALUES.FREEZE_3:
                return 'Freeze 3';
              case FREEZE_OPTION_VALUES.FREEZE_4:
                return 'Freeze 4';
              case FREEZE_OPTION_VALUES.FREEZE_5:
                return 'Freeze 5';
              case FREEZE_OPTION_VALUES.FREEZE_6:
                return 'Freeze 6';
              case FREEZE_OPTION_VALUES.FREEZE_7:
                return 'Freeze 7';
              case FREEZE_OPTION_VALUES.FREEZE_8:
                return 'Freeze 8';
              case FREEZE_OPTION_VALUES.FREEZE_9:
                return 'Freeze 9';
              case FREEZE_OPTION_VALUES.FREEZE_10:
                return 'Freeze 10';

              default:
                return params.value.toString();
            }
          },
          defaultToNothingSelected: true,
          suppressSelectAll: false,
        },
        valueGetter: (params) => {
          const freezeValue = params.data?.freeze;
          if (!freezeValue) return '';

          switch (freezeValue) {
            case FREEZE_OPTION_VALUES.FREEZE:
              return 'Freeze';
            case FREEZE_OPTION_VALUES.FREEZE_ONLY:
              return 'Freeze Only';
            case FREEZE_OPTION_VALUES.FREEZE_1:
              return 'Freeze 1';
            case FREEZE_OPTION_VALUES.FREEZE_2:
              return 'Freeze 2';
            case FREEZE_OPTION_VALUES.FREEZE_3:
              return 'Freeze 3';
            case FREEZE_OPTION_VALUES.FREEZE_4:
              return 'Freeze 4';
            case FREEZE_OPTION_VALUES.FREEZE_5:
              return 'Freeze 5';
            case FREEZE_OPTION_VALUES.FREEZE_6:
              return 'Freeze 6';
            case FREEZE_OPTION_VALUES.FREEZE_7:
              return 'Freeze 7';
            case FREEZE_OPTION_VALUES.FREEZE_8:
              return 'Freeze 8';
            case FREEZE_OPTION_VALUES.FREEZE_9:
              return 'Freeze 9';
            case FREEZE_OPTION_VALUES.FREEZE_10:
              return 'Freeze 10';
            default:
              return freezeValue.toString();
          }
        },
      },
      ...(isExportColumnEnabled
        ? ([
            {
              field: 'export',
              headerName: 'Export',
              width: 100,
              filter: 'agSetColumnFilter',
              filterParams: {
                values: [null, ...exportOptions.map((option) => option.value)],
                valueFormatter: (params: { value: number | null }) =>
                  params.value == null
                    ? 'Blank'
                    : (exportLabels.get(params.value) ??
                      params.value.toString()),
                defaultToNothingSelected: true,
              },
              valueFormatter: (params) =>
                params.value == null
                  ? ''
                  : (exportLabels.get(params.value) ?? params.value.toString()),
            },
          ] satisfies ColDef<DonorSchedule>[])
        : []),
      {
        field: 'transfer',
        headerName: 'Transfer?',
        width: 80,
        filter: 'agSetColumnFilter',
        filterParams: {
          values: [
            TRANSFER_OPTION_VALUES.TRANSFER_ALL,
            TRANSFER_OPTION_VALUES.TRANSFER_LOW,
            TRANSFER_OPTION_VALUES.TRANSFER_1,
            TRANSFER_OPTION_VALUES.TRANSFER_2,
            TRANSFER_OPTION_VALUES.TRANSFER_3,
            TRANSFER_OPTION_VALUES.TRANSFER_4,
            TRANSFER_OPTION_VALUES.TRANSFER_5,
            TRANSFER_OPTION_VALUES.TRANSFER_6,
            TRANSFER_OPTION_VALUES.TRANSFER_7,
            TRANSFER_OPTION_VALUES.TRANSFER_8,
            TRANSFER_OPTION_VALUES.TRANSFER_9,
            TRANSFER_OPTION_VALUES.TRANSFER_10,
          ],
          valueFormatter: (params: { value: number }) => {
            switch (params.value) {
              case TRANSFER_OPTION_VALUES.TRANSFER_ALL:
                return 'Transfer All';
              case TRANSFER_OPTION_VALUES.TRANSFER_LOW:
                return 'Transfer Low';
              case TRANSFER_OPTION_VALUES.TRANSFER_1:
                return 'Transfer 1';
              case TRANSFER_OPTION_VALUES.TRANSFER_2:
                return 'Transfer 2';
              case TRANSFER_OPTION_VALUES.TRANSFER_3:
                return 'Transfer 3';
              case TRANSFER_OPTION_VALUES.TRANSFER_4:
                return 'Transfer 4';
              case TRANSFER_OPTION_VALUES.TRANSFER_5:
                return 'Transfer 5';
              case TRANSFER_OPTION_VALUES.TRANSFER_6:
                return 'Transfer 6';
              case TRANSFER_OPTION_VALUES.TRANSFER_7:
                return 'Transfer 7';
              case TRANSFER_OPTION_VALUES.TRANSFER_8:
                return 'Transfer 8';
              case TRANSFER_OPTION_VALUES.TRANSFER_9:
                return 'Transfer 9';
              case TRANSFER_OPTION_VALUES.TRANSFER_10:
                return 'Transfer 10';
              default:
                return params.value.toString();
            }
          },
          defaultToNothingSelected: true,
          suppressSelectAll: false,
        },
        valueGetter: (params) => {
          const transferValue = params.data?.transfer;
          if (!transferValue) return '';

          switch (transferValue) {
            case TRANSFER_OPTION_VALUES.TRANSFER_ALL:
              return 'Transfer All';
            case TRANSFER_OPTION_VALUES.TRANSFER_LOW:
              return 'Transfer Low';
            case TRANSFER_OPTION_VALUES.TRANSFER_1:
              return 'Transfer 1';
            case TRANSFER_OPTION_VALUES.TRANSFER_2:
              return 'Transfer 2';
            case TRANSFER_OPTION_VALUES.TRANSFER_3:
              return 'Transfer 3';
            case TRANSFER_OPTION_VALUES.TRANSFER_4:
              return 'Transfer 4';
            case TRANSFER_OPTION_VALUES.TRANSFER_5:
              return 'Transfer 5';
            case TRANSFER_OPTION_VALUES.TRANSFER_6:
              return 'Transfer 6';
            case TRANSFER_OPTION_VALUES.TRANSFER_7:
              return 'Transfer 7';
            case TRANSFER_OPTION_VALUES.TRANSFER_8:
              return 'Transfer 8';
            case TRANSFER_OPTION_VALUES.TRANSFER_9:
              return 'Transfer 9';
            case TRANSFER_OPTION_VALUES.TRANSFER_10:
              return 'Transfer 10';
            default:
              return transferValue.toString();
          }
        },
      },
      {
        field: 'colCode',
        headerName: 'Collection Code',
        filter: 'agTextColumnFilter',
        width: 80,
      },
      {
        field: 'semenLocation.name',
        headerName: 'Semen Location',
        filter: 'agTextColumnFilter',
        width: 100,
      },
      {
        field: 'semenOwner.name',
        headerName: 'Semen Owner',
        filter: 'agTextColumnFilter',
        width: 120,
      },
      {
        field: 'semenComment',
        headerName: 'Semen Comment',
        filter: 'agTextColumnFilter',
        width: 100,
      },
      ...(isAddColumnsEnabled
        ? [
            {
              field: 'donor.nm',
              headerName: 'Donor Net Merit',
              wrapHeaderText: true,
              sortable: true,
              resizable: true,
              initialWidth: 100,
              filter: 'agNumberColumnFilter',
            },
            {
              field: 'donor.psi',
              headerName: 'Donor PSI',
              wrapHeaderText: true,
              sortable: true,
              resizable: true,
              initialWidth: 100,
              filter: 'agNumberColumnFilter',
            },
            {
              field: 'priority',
              headerName: 'Mating Priority',
              wrapHeaderText: true,
              sortable: true,
              resizable: true,
              initialWidth: 80,
              filter: 'agNumberColumnFilter',
            },
            {
              field: 'donor.birth',
              headerName: 'Donor Birthdate',
              wrapHeaderText: true,
              sortable: true,
              resizable: true,
              initialWidth: 100,
              filter: 'agDateColumnFilter',
              valueFormatter: dateFormatter,
            },
            {
              field: 'specialInstructions',
              headerName: 'Aspiration Comments',
              wrapHeaderText: true,
              sortable: true,
              resizable: true,
              initialWidth: 120,
              filter: 'agTextColumnFilter',
            },
            {
              field: 'donorComment',
              headerName: 'Donor Comments',
              wrapHeaderText: true,
              sortable: true,
              resizable: true,
              initialWidth: 120,
              filter: 'agTextColumnFilter',
            },
            {
              field: 'semen.naab',
              headerName: 'Semen NAAB',
              wrapHeaderText: true,
              sortable: true,
              resizable: true,
              initialWidth: 100,
              filter: 'agTextColumnFilter',
            },
            {
              field: 'semen.sex',
              headerName: 'Sex (Semen)',
              wrapHeaderText: true,
              sortable: true,
              resizable: true,
              initialWidth: 80,
              valueFormatter: (params: { value?: number | string | null }) =>
                formatSemenSex(params.value, isSemenConventionalTypeEnabled),
              filter: 'agSetColumnFilter',
              filterParams: {
                values: [SEX.Male, SEX.Female, SEX.Unknown],
                valueFormatter: (params: { value: number }) =>
                  formatSemenSex(params.value, isSemenConventionalTypeEnabled),
              },
            },
            {
              field: 'donor.breed.name',
              headerName: 'Donor Breed',
              wrapHeaderText: true,
              sortable: true,
              resizable: true,
              initialWidth: 100,
              filter: 'agTextColumnFilter',
            },
            isRest
              ? {
                  field: 'donor.breed.subFamily',
                  headerName: 'Donor Species',
                  wrapHeaderText: true,
                  sortable: true,
                  resizable: true,
                  initialWidth: 100,
                  hide: true,
                  filter: 'agSetColumnFilter',
                  filterParams: {
                    values: subfamilyValues,
                    valueFormatter: (params: { value: number }) =>
                      subfamilyLabels[params.value] ?? params.value,
                  },
                  valueFormatter: (params) =>
                    params.data?.donor?.breed?.subFamilyValue ?? '',
                }
              : {
                  field: 'donor.breed.subFamily',
                  headerName: 'Donor Species',
                  wrapHeaderText: true,
                  sortable: true,
                  resizable: true,
                  initialWidth: 100,
                  hide: true,
                  filter: 'agSetColumnFilter',
                  filterParams: {
                    values: SUBFAMILY_FILTER_VALUES,
                    valueFormatter: (params: { value: unknown }) =>
                      SUBFAMILY_LABELS[String(params.value)] ??
                      String(params.value ?? ''),
                  },
                  valueFormatter: (params) =>
                    SUBFAMILY_LABELS[String(params.value)] ??
                    params.value ??
                    '',
                },
            {
              field: 'sire.breed.name',
              headerName: 'Sire Breed',
              wrapHeaderText: true,
              sortable: true,
              resizable: true,
              initialWidth: 100,
              filter: 'agTextColumnFilter',
            },
            isRest
              ? {
                  field: 'sire.breed.subFamily',
                  headerName: 'Sire Species',
                  wrapHeaderText: true,
                  sortable: true,
                  resizable: true,
                  initialWidth: 100,
                  hide: true,
                  filter: 'agSetColumnFilter',
                  filterParams: {
                    values: subfamilyValues,
                    valueFormatter: (params: { value: number }) =>
                      subfamilyLabels[params.value] ?? params.value,
                  },
                  valueFormatter: (params) =>
                    params.data?.sire?.breed?.subFamilyValue ?? '',
                }
              : {
                  field: 'sire.breed.subFamily',
                  headerName: 'Sire Species',
                  wrapHeaderText: true,
                  sortable: true,
                  resizable: true,
                  initialWidth: 100,
                  hide: true,
                  filter: 'agSetColumnFilter',
                  filterParams: {
                    values: SUBFAMILY_FILTER_VALUES,
                    valueFormatter: (params: { value: unknown }) =>
                      SUBFAMILY_LABELS[String(params.value)] ??
                      String(params.value ?? ''),
                  },
                  valueFormatter: (params) =>
                    SUBFAMILY_LABELS[String(params.value)] ??
                    params.value ??
                    '',
                },
            {
              field: 'tt',
              headerName: 'Trypsin Treat (TT)',
              wrapHeaderText: true,
              sortable: true,
              resizable: true,
              initialWidth: 80,
              filter: 'agSetColumnFilter',
              filterParams: {
                values: [true, false],
                valueFormatter: (params: { value: boolean }) =>
                  params.value ? 'Yes' : 'No',
                defaultToNothingSelected: true,
              },
              cellRenderer: CheckmarkValueRenderer,
              cellClass: 'custom-cell__checkmark',
            },
            {
              field: 'freezeMethod.label',
              headerName: 'Freeze Method',
              wrapHeaderText: true,
              sortable: true,
              resizable: true,
              initialWidth: 100,
              filter: 'agTextColumnFilter',
            },
            {
              field: 'pool',
              headerName: 'Pool Y/N',
              wrapHeaderText: true,
              sortable: true,
              resizable: true,
              initialWidth: 80,
              filter: 'agSetColumnFilter',
              filterParams: {
                values: [true, false],
                valueFormatter: (params: { value: boolean }) =>
                  params.value ? 'Yes' : 'No',
                defaultToNothingSelected: true,
              },
              cellRenderer: CheckmarkValueRenderer,
              cellClass: 'custom-cell__checkmark',
            },
            {
              field: 'pregnant',
              headerName: 'Pregnant Y/N',
              wrapHeaderText: true,
              sortable: true,
              resizable: true,
              initialWidth: 80,
              valueFormatter: pregnantFormatter,
              filter: 'agSetColumnFilter',
              filterParams: {
                values: [PREGNANT_OPTION_VALUES.NO, PREGNANT_OPTION_VALUES.YES],
                valueFormatter: (params: { value: number }) => {
                  const v = Number(params.value);
                  if (v === PREGNANT_OPTION_VALUES.NO) return 'No';
                  if (v === PREGNANT_OPTION_VALUES.YES) return 'Yes';
                  return '';
                },
                defaultToNothingSelected: true,
              },
            },
            {
              field: 'owner.name',
              headerName: 'Account',
              wrapHeaderText: true,
              sortable: true,
              resizable: true,
              initialWidth: 150,
              filter: 'agTextColumnFilter',
            },
            {
              field: 'createdBy.fullName',
              headerName: 'Created By',
              wrapHeaderText: true,
              sortable: true,
              resizable: true,
              initialWidth: 120,
              filter: 'agTextColumnFilter',
            },
            {
              field: 'drug2.name',
              headerName: 'FSH Drug 2',
              wrapHeaderText: true,
              sortable: true,
              resizable: true,
              initialWidth: 100,
              filter: 'agTextColumnFilter',
            },
            {
              field: 'nonEUCountry',
              headerName: 'Non-EU Country',
              wrapHeaderText: true,
              sortable: true,
              resizable: true,
              initialWidth: 100,
              valueFormatter: nonEuCountryFormatter,
              filter: 'agSetColumnFilter',
              filterParams: {
                values: Object.values(NON_EU_COUNTRY_VALUES),
                valueFormatter: (params: { value: number }) =>
                  nonEuCountryFormatter({ value: params.value }),
                defaultToNothingSelected: true,
              },
            },
          ]
        : []),
    ] as ColDef<DonorSchedule>[];
  }, [
    exportStringMap.options,
    isAddColumnsEnabled,
    isExportColumnEnabled,
    isInactiveLookupEnabled,
    isRest,
    isSemenConventionalTypeEnabled,
    subfamilyOptions,
  ]);
}
